import { ErrorTagUtil } from '@onshape/www-inertia';
import ErrorUtil from '../../../../../util/ErrorUtil';
import type { UnifiedSignupScreenName } from '../UnifiedSignupScreenName';
import UnifiedSignupEmailType from './UnifiedSignupEmailType';
import UnifiedSignupIntent from './UnifiedSignupIntent';
import UnifiedSignupPersona from './UnifiedSignupPersona';
import UnifiedSignupPlan from './UnifiedSignupPlan';
import UnifiedSignupStartScreen from './UnifiedSignupStartScreen';

export interface IUnifiedSignupChoosePlanCards {
  readonly screenName: UnifiedSignupScreenName | null;

  readonly plan: UnifiedSignupPlan | null;
}

type PlanSpec = Array<
  [
    UnifiedSignupStartScreen,
    UnifiedSignupEmailType,
    UnifiedSignupIntent,
    UnifiedSignupPersona,
    IUnifiedSignupChoosePlanCards,
  ]
>;

export default class UnifiedSignupChoosePlanCards implements IUnifiedSignupChoosePlanCards {
  public readonly screenName: UnifiedSignupScreenName | null;

  public readonly plan: UnifiedSignupPlan | null;

  public static readonly chooseProFree = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-choose-pro-free',
  });

  public static readonly chooseProEdu = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-choose-pro-edu',
  });

  public static readonly chooseProFreeEdu = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-choose-pro-free-edu',
  });

  public static readonly chooseProFreeEducator = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-choose-pro-free-educator',
  });

  public static readonly chooseHlfreeProEdu = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-choose-hlfree-pro-edu',
  });

  public static readonly chooseFreeHlproEdu = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-choose-free-hlpro-edu',
  });

  public static readonly chooseFreeEdu = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-choose-free-edu',
  });

  public static readonly chooseFreeEducator = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-choose-free-educator',
  });

  public static readonly explainPro = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-explain-pro',
  });

  public static readonly explainFree = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-explain-free',
  });

  public static readonly explainEdu = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-explain-edu',
  });

  public static readonly explainEducator = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-explain-educator',
  });

  public static readonly divertEdu = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-divert-edu',
  });

  public static readonly divertEducator = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-divert-educator',
  });

  public static readonly divertPro = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-divert-pro',
  });

  public static readonly divertFree = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-divert-free',
  });

  public static readonly seeOtherOptions = new UnifiedSignupChoosePlanCards({
    screenName: 'uss-see-other-options',
  });

  public static readonly skipFree = new UnifiedSignupChoosePlanCards({
    plan: UnifiedSignupPlan.free,
  });

  public static readonly skipPro = new UnifiedSignupChoosePlanCards({
    plan: UnifiedSignupPlan.pro,
  });

  public static readonly skipEdu = new UnifiedSignupChoosePlanCards({
    plan: UnifiedSignupPlan.edu,
  });

  public static readonly skipEducator = new UnifiedSignupChoosePlanCards({
    plan: UnifiedSignupPlan.educator,
  });

  /** Placeholder screen page */
  public static readonly notImplemented = new UnifiedSignupChoosePlanCards({
    screenName: 'not-implemented',
  });

  private constructor({
    screenName,
    plan,
  }: {
    screenName?: UnifiedSignupScreenName;
    plan?: UnifiedSignupPlan;
  }) {
    this.screenName = screenName || null;
    this.plan = plan || null;
  }

  public static getInstance({
    startScreen,
    emailType,
    intent,
    persona,
    isEduRenew,
  }: {
    startScreen: UnifiedSignupStartScreen;
    emailType: UnifiedSignupEmailType;
    intent: UnifiedSignupIntent;
    persona: UnifiedSignupPersona;
    isEduRenew: boolean;
  }): IUnifiedSignupChoosePlanCards {
    if (isEduRenew) {
      return UnifiedSignupChoosePlanCards.skipEdu;
    }

    const found = UnifiedSignupChoosePlanCards.buildSpec()
      .filter((spec) => {
        if (!spec[0].compareTo(startScreen)) return false;
        if (!spec[1].compareTo(emailType)) return false;
        if (!spec[2].compareTo(intent)) return false;
        if (!spec[3].compareTo(persona)) return false;
        return true;
      })
      .map((spec) => spec[4]);

    const instance = found[0];
    if (instance?.screenName === 'not-implemented') {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateUnifiedSignup,
        msg: 'notImplemented choose plan screen found',
      });
      throw new Error('Not Implemented');
    }
    return instance as IUnifiedSignupChoosePlanCards;
  }

  public static findByName(screenName: string): IUnifiedSignupChoosePlanCards | null {
    const found = UnifiedSignupChoosePlanCards.buildSpec().find(
      (spec) => spec[4].screenName === screenName
    );
    return found ? found[4] : null;
  }

  private static buildSpec(): PlanSpec {
    const {
      genericSignup,
      homepageSignup,
      buyPro,
      buyStandard,
      eduPlan,
      freePlan,
      landingPage,
      trialPlan,
    } = UnifiedSignupStartScreen;
    const { genEmail, nonGenEmail } = UnifiedSignupEmailType;
    const { bizIntent, eduIntent, funIntent } = UnifiedSignupIntent;
    const { proPerson, educatorPerson, studentPerson, hobbyPerson } = UnifiedSignupPersona;

    const {
      chooseFreeHlproEdu,
      chooseHlfreeProEdu,
      chooseProEdu,
      chooseProFreeEducator,
      chooseFreeEducator,
      divertEducator,
      chooseProFree,
      explainEdu,
      explainEducator,
      explainFree,
      explainPro,
      divertEdu,
      divertFree,
      divertPro,
      skipEdu,
      skipFree,
      skipPro,
      skipEducator,
      notImplemented,
    } = UnifiedSignupChoosePlanCards;

    // see helper script at end of file
    return [
      [genericSignup, genEmail, bizIntent, proPerson, explainPro],
      [genericSignup, genEmail, bizIntent, educatorPerson, explainPro],
      [genericSignup, genEmail, bizIntent, studentPerson, chooseProEdu],
      [genericSignup, genEmail, bizIntent, hobbyPerson, explainPro],
      [genericSignup, genEmail, eduIntent, proPerson, notImplemented],
      [genericSignup, genEmail, eduIntent, educatorPerson, explainEducator],
      [genericSignup, genEmail, eduIntent, studentPerson, explainEdu],
      [genericSignup, genEmail, eduIntent, hobbyPerson, notImplemented],
      [genericSignup, genEmail, funIntent, proPerson, explainPro],
      [genericSignup, genEmail, funIntent, educatorPerson, explainEducator],
      [genericSignup, genEmail, funIntent, studentPerson, explainEdu],
      [genericSignup, genEmail, funIntent, hobbyPerson, explainFree],
      [genericSignup, nonGenEmail, bizIntent, proPerson, explainPro],
      [genericSignup, nonGenEmail, bizIntent, educatorPerson, explainPro],
      [genericSignup, nonGenEmail, bizIntent, studentPerson, explainPro],
      [genericSignup, nonGenEmail, bizIntent, hobbyPerson, explainPro],
      [genericSignup, nonGenEmail, eduIntent, proPerson, notImplemented],
      [genericSignup, nonGenEmail, eduIntent, educatorPerson, explainEducator],
      [genericSignup, nonGenEmail, eduIntent, studentPerson, explainEdu],
      [genericSignup, nonGenEmail, eduIntent, hobbyPerson, notImplemented],
      [genericSignup, nonGenEmail, funIntent, proPerson, explainPro],
      [genericSignup, nonGenEmail, funIntent, educatorPerson, explainEducator],
      [genericSignup, nonGenEmail, funIntent, studentPerson, explainEdu],
      [genericSignup, nonGenEmail, funIntent, hobbyPerson, chooseProFree],
      [homepageSignup, genEmail, bizIntent, proPerson, explainPro],
      [homepageSignup, genEmail, bizIntent, educatorPerson, explainPro],
      [homepageSignup, genEmail, bizIntent, studentPerson, chooseProEdu],
      [homepageSignup, genEmail, bizIntent, hobbyPerson, explainPro],
      [homepageSignup, genEmail, eduIntent, proPerson, notImplemented],
      [homepageSignup, genEmail, eduIntent, educatorPerson, explainEducator],
      [homepageSignup, genEmail, eduIntent, studentPerson, explainEdu],
      [homepageSignup, genEmail, eduIntent, hobbyPerson, notImplemented],
      [homepageSignup, genEmail, funIntent, proPerson, explainPro],
      [homepageSignup, genEmail, funIntent, educatorPerson, explainEducator],
      [homepageSignup, genEmail, funIntent, studentPerson, explainEdu],
      [homepageSignup, genEmail, funIntent, hobbyPerson, explainFree],
      [homepageSignup, nonGenEmail, bizIntent, proPerson, explainPro],
      [homepageSignup, nonGenEmail, bizIntent, educatorPerson, explainPro],
      [homepageSignup, nonGenEmail, bizIntent, studentPerson, explainPro],
      [homepageSignup, nonGenEmail, bizIntent, hobbyPerson, explainPro],
      [homepageSignup, nonGenEmail, eduIntent, proPerson, notImplemented],
      [homepageSignup, nonGenEmail, eduIntent, educatorPerson, explainEducator],
      [homepageSignup, nonGenEmail, eduIntent, studentPerson, explainEdu],
      [homepageSignup, nonGenEmail, eduIntent, hobbyPerson, notImplemented],
      [homepageSignup, nonGenEmail, funIntent, proPerson, explainPro],
      [homepageSignup, nonGenEmail, funIntent, educatorPerson, explainEducator],
      [homepageSignup, nonGenEmail, funIntent, studentPerson, explainEdu],
      [homepageSignup, nonGenEmail, funIntent, hobbyPerson, chooseProFree],
      [eduPlan, genEmail, bizIntent, proPerson, notImplemented],
      [eduPlan, genEmail, bizIntent, educatorPerson, notImplemented],
      [eduPlan, genEmail, bizIntent, studentPerson, notImplemented],
      [eduPlan, genEmail, bizIntent, hobbyPerson, notImplemented],
      [eduPlan, genEmail, eduIntent, proPerson, notImplemented],
      [eduPlan, genEmail, eduIntent, educatorPerson, skipEducator],
      [eduPlan, genEmail, eduIntent, studentPerson, skipEdu],
      [eduPlan, genEmail, eduIntent, hobbyPerson, notImplemented],
      [eduPlan, genEmail, funIntent, proPerson, notImplemented],
      [eduPlan, genEmail, funIntent, educatorPerson, notImplemented],
      [eduPlan, genEmail, funIntent, studentPerson, notImplemented],
      [eduPlan, genEmail, funIntent, hobbyPerson, notImplemented],
      [eduPlan, nonGenEmail, bizIntent, proPerson, notImplemented],
      [eduPlan, nonGenEmail, bizIntent, educatorPerson, notImplemented],
      [eduPlan, nonGenEmail, bizIntent, studentPerson, notImplemented],
      [eduPlan, nonGenEmail, bizIntent, hobbyPerson, notImplemented],
      [eduPlan, nonGenEmail, eduIntent, proPerson, notImplemented],
      [eduPlan, nonGenEmail, eduIntent, educatorPerson, skipEducator],
      [eduPlan, nonGenEmail, eduIntent, studentPerson, skipEdu],
      [eduPlan, nonGenEmail, eduIntent, hobbyPerson, notImplemented],
      [eduPlan, nonGenEmail, funIntent, proPerson, notImplemented],
      [eduPlan, nonGenEmail, funIntent, educatorPerson, notImplemented],
      [eduPlan, nonGenEmail, funIntent, studentPerson, notImplemented],
      [eduPlan, nonGenEmail, funIntent, hobbyPerson, notImplemented],
      [freePlan, genEmail, bizIntent, proPerson, divertPro],
      [freePlan, genEmail, bizIntent, educatorPerson, chooseProFreeEducator],
      [freePlan, genEmail, bizIntent, studentPerson, chooseHlfreeProEdu],
      [freePlan, genEmail, bizIntent, hobbyPerson, divertPro],
      [freePlan, genEmail, eduIntent, proPerson, notImplemented],
      [freePlan, genEmail, eduIntent, educatorPerson, divertEducator],
      [freePlan, genEmail, eduIntent, studentPerson, divertEdu],
      [freePlan, genEmail, eduIntent, hobbyPerson, notImplemented],
      [freePlan, genEmail, funIntent, proPerson, divertPro],
      [freePlan, genEmail, funIntent, educatorPerson, chooseFreeEducator],
      [freePlan, genEmail, funIntent, studentPerson, divertEdu],
      [freePlan, genEmail, funIntent, hobbyPerson, skipFree],
      [freePlan, nonGenEmail, bizIntent, proPerson, divertPro],
      [freePlan, nonGenEmail, bizIntent, educatorPerson, divertPro],
      [freePlan, nonGenEmail, bizIntent, studentPerson, chooseFreeHlproEdu],
      [freePlan, nonGenEmail, bizIntent, hobbyPerson, divertPro],
      [freePlan, nonGenEmail, eduIntent, proPerson, notImplemented],
      [freePlan, nonGenEmail, eduIntent, educatorPerson, divertEducator],
      [freePlan, nonGenEmail, eduIntent, studentPerson, divertEdu],
      [freePlan, nonGenEmail, eduIntent, hobbyPerson, notImplemented],
      [freePlan, nonGenEmail, funIntent, proPerson, divertPro],
      [freePlan, nonGenEmail, funIntent, educatorPerson, chooseFreeEducator],
      [freePlan, nonGenEmail, funIntent, studentPerson, divertEdu],
      [freePlan, nonGenEmail, funIntent, hobbyPerson, chooseProFree],
      [trialPlan, genEmail, bizIntent, proPerson, skipPro],
      [trialPlan, genEmail, bizIntent, educatorPerson, skipPro],
      [trialPlan, genEmail, bizIntent, studentPerson, chooseProEdu],
      [trialPlan, genEmail, bizIntent, hobbyPerson, skipPro],
      [trialPlan, genEmail, eduIntent, proPerson, notImplemented],
      [trialPlan, genEmail, eduIntent, educatorPerson, divertEducator],
      [trialPlan, genEmail, eduIntent, studentPerson, divertEdu],
      [trialPlan, genEmail, eduIntent, hobbyPerson, notImplemented],
      [trialPlan, genEmail, funIntent, proPerson, explainPro],
      [trialPlan, genEmail, funIntent, educatorPerson, divertEducator],
      [trialPlan, genEmail, funIntent, studentPerson, divertEdu],
      [trialPlan, genEmail, funIntent, hobbyPerson, divertFree],
      [trialPlan, nonGenEmail, bizIntent, proPerson, skipPro],
      [trialPlan, nonGenEmail, bizIntent, educatorPerson, skipPro],
      [trialPlan, nonGenEmail, bizIntent, studentPerson, skipPro],
      [trialPlan, nonGenEmail, bizIntent, hobbyPerson, skipPro],
      [trialPlan, nonGenEmail, eduIntent, proPerson, notImplemented],
      [trialPlan, nonGenEmail, eduIntent, educatorPerson, divertEducator],
      [trialPlan, nonGenEmail, eduIntent, studentPerson, divertEdu],
      [trialPlan, nonGenEmail, eduIntent, hobbyPerson, notImplemented],
      [trialPlan, nonGenEmail, funIntent, proPerson, explainPro],
      [trialPlan, nonGenEmail, funIntent, educatorPerson, divertEducator],
      [trialPlan, nonGenEmail, funIntent, studentPerson, divertEdu],
      [trialPlan, nonGenEmail, funIntent, hobbyPerson, skipPro],

      [buyPro, genEmail, bizIntent, proPerson, notImplemented],
      [buyPro, genEmail, bizIntent, educatorPerson, notImplemented],
      [buyPro, genEmail, bizIntent, studentPerson, notImplemented],
      [buyPro, genEmail, bizIntent, hobbyPerson, notImplemented],
      [buyPro, genEmail, eduIntent, proPerson, notImplemented],
      [buyPro, genEmail, eduIntent, educatorPerson, notImplemented],
      [buyPro, genEmail, eduIntent, studentPerson, notImplemented],
      [buyPro, genEmail, eduIntent, hobbyPerson, notImplemented],
      [buyPro, genEmail, funIntent, proPerson, notImplemented],
      [buyPro, genEmail, funIntent, educatorPerson, notImplemented],
      [buyPro, genEmail, funIntent, studentPerson, notImplemented],
      [buyPro, genEmail, funIntent, hobbyPerson, notImplemented],
      [buyPro, nonGenEmail, bizIntent, proPerson, notImplemented],
      [buyPro, nonGenEmail, bizIntent, educatorPerson, notImplemented],
      [buyPro, nonGenEmail, bizIntent, studentPerson, notImplemented],
      [buyPro, nonGenEmail, bizIntent, hobbyPerson, notImplemented],
      [buyPro, nonGenEmail, eduIntent, proPerson, notImplemented],
      [buyPro, nonGenEmail, eduIntent, educatorPerson, notImplemented],
      [buyPro, nonGenEmail, eduIntent, studentPerson, notImplemented],
      [buyPro, nonGenEmail, eduIntent, hobbyPerson, notImplemented],
      [buyPro, nonGenEmail, funIntent, proPerson, notImplemented],
      [buyPro, nonGenEmail, funIntent, educatorPerson, notImplemented],
      [buyPro, nonGenEmail, funIntent, studentPerson, notImplemented],
      [buyPro, nonGenEmail, funIntent, hobbyPerson, notImplemented],
      [buyStandard, genEmail, bizIntent, proPerson, notImplemented],
      [buyStandard, genEmail, bizIntent, educatorPerson, notImplemented],
      [buyStandard, genEmail, bizIntent, studentPerson, notImplemented],
      [buyStandard, genEmail, bizIntent, hobbyPerson, notImplemented],
      [buyStandard, genEmail, eduIntent, proPerson, notImplemented],
      [buyStandard, genEmail, eduIntent, educatorPerson, notImplemented],
      [buyStandard, genEmail, eduIntent, studentPerson, notImplemented],
      [buyStandard, genEmail, eduIntent, hobbyPerson, notImplemented],
      [buyStandard, genEmail, funIntent, proPerson, notImplemented],
      [buyStandard, genEmail, funIntent, educatorPerson, notImplemented],
      [buyStandard, genEmail, funIntent, studentPerson, notImplemented],
      [buyStandard, genEmail, funIntent, hobbyPerson, notImplemented],
      [buyStandard, nonGenEmail, bizIntent, proPerson, notImplemented],
      [buyStandard, nonGenEmail, bizIntent, educatorPerson, notImplemented],
      [buyStandard, nonGenEmail, bizIntent, studentPerson, notImplemented],
      [buyStandard, nonGenEmail, bizIntent, hobbyPerson, notImplemented],
      [buyStandard, nonGenEmail, eduIntent, proPerson, notImplemented],
      [buyStandard, nonGenEmail, eduIntent, educatorPerson, notImplemented],
      [buyStandard, nonGenEmail, eduIntent, studentPerson, notImplemented],
      [buyStandard, nonGenEmail, eduIntent, hobbyPerson, notImplemented],
      [buyStandard, nonGenEmail, funIntent, proPerson, notImplemented],
      [buyStandard, nonGenEmail, funIntent, educatorPerson, notImplemented],
      [buyStandard, nonGenEmail, funIntent, studentPerson, notImplemented],
      [buyStandard, nonGenEmail, funIntent, hobbyPerson, notImplemented],
      [landingPage, genEmail, bizIntent, proPerson, skipPro],
      [landingPage, genEmail, bizIntent, educatorPerson, skipPro],
      [landingPage, genEmail, bizIntent, studentPerson, chooseProEdu],
      [landingPage, genEmail, bizIntent, hobbyPerson, skipPro],
      [landingPage, genEmail, eduIntent, proPerson, notImplemented],
      [landingPage, genEmail, eduIntent, educatorPerson, divertEdu],
      [landingPage, genEmail, eduIntent, studentPerson, divertEdu],
      [landingPage, genEmail, eduIntent, hobbyPerson, notImplemented],
      [landingPage, genEmail, funIntent, proPerson, explainPro],
      [landingPage, genEmail, funIntent, educatorPerson, divertEdu],
      [landingPage, genEmail, funIntent, studentPerson, divertEdu],
      [landingPage, genEmail, funIntent, hobbyPerson, divertFree],
      [landingPage, nonGenEmail, bizIntent, proPerson, skipPro],
      [landingPage, nonGenEmail, bizIntent, educatorPerson, skipPro],
      [landingPage, nonGenEmail, bizIntent, studentPerson, skipPro],
      [landingPage, nonGenEmail, bizIntent, hobbyPerson, skipPro],
      [landingPage, nonGenEmail, eduIntent, proPerson, notImplemented],
      [landingPage, nonGenEmail, eduIntent, educatorPerson, divertEdu],
      [landingPage, nonGenEmail, eduIntent, studentPerson, divertEdu],
      [landingPage, nonGenEmail, eduIntent, hobbyPerson, notImplemented],
      [landingPage, nonGenEmail, funIntent, proPerson, explainPro],
      [landingPage, nonGenEmail, funIntent, educatorPerson, divertEdu],
      [landingPage, nonGenEmail, funIntent, studentPerson, divertEdu],
      [landingPage, nonGenEmail, funIntent, hobbyPerson, skipPro],
    ];
  }
}
